/**
 * Iframe Communication Utility
 * 
 * Handles postMessage communication between the gallery (iframe) and parent portal.
 * Enables real-time configuration updates from the customer portal.
 */

/** Cached parent origin from received messages */
let _cachedParentOrigin = null;

/**
 * Resolve the explicit allow-list of parent origins permitted to communicate
 * with the gallery iframe. Reads REACT_APP_ALLOWED_ORIGINS (comma-separated).
 * Falls back to the known portal origins in production, and to '*' only during
 * local development when nothing is configured (never silently in production).
 * @returns {string|Array<string>} '*' or an array of explicit origins
 */
export const getAllowedOrigins = () => {
  const fromEnv = (process.env.REACT_APP_ALLOWED_ORIGINS || '').trim();
  if (fromEnv) {
    return fromEnv
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);
  }
  // No env configured: allow '*' only outside production for local dev convenience.
  if (process.env.NODE_ENV !== 'production') {
    return '*';
  }
  // Keep the previous production hostname during the canonical-domain transition.
  return [
    'https://portal-wedlinx.catalinx.space',
    'https://wedlinx-portal.catalinx.space',
    'https://dev-portal-wedlinx.catalinx.space',
  ];
};

/**
 * Message type constants
 */
export const MESSAGE_TYPES = {
  // Incoming messages (Portal → Gallery)
  UPDATE_THEME: 'UPDATE_THEME',
  UPDATE_LOGO: 'UPDATE_LOGO',
  UPDATE_TABS: 'UPDATE_TABS',
  GET_CURRENT_CONFIG: 'GET_CURRENT_CONFIG',
  CONFIG_DEPLOYED: 'CONFIG_DEPLOYED',
  
  // Outgoing messages (Gallery → Portal)
  THEME_UPDATED: 'THEME_UPDATED',
  LOGO_UPDATED: 'LOGO_UPDATED',
  TABS_UPDATED: 'TABS_UPDATED',
  CONFIG_RESPONSE: 'CONFIG_RESPONSE',
  ERROR: 'ERROR',
  GALLERY_READY: 'GALLERY_READY',
};

/**
 * Check if the app is running inside an iframe
 * @returns {boolean} True if running in iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin iframe will throw error, which means we're in an iframe
    return true;
  }
};

/**
 * Send a message to the parent window
 * @param {string} type - Message type from MESSAGE_TYPES
 * @param {Object} data - Message payload
 * @param {string} targetOrigin - Concrete target origin for postMessage. Defaults
 *   to the cached parent origin captured from a validated incoming message.
 */
export const sendMessageToParent = (type, data = {}, targetOrigin = _cachedParentOrigin) => {
  if (!isInIframe()) {
    console.warn('sendMessageToParent: Not in iframe, message not sent');
    return;
  }

  if (!targetOrigin) {
    // No concrete origin known (e.g. handshake before any parent message and no
    // configured allow-list). Refuse to broadcast silently.
    console.warn('sendMessageToParent: no concrete targetOrigin available, message not sent', { type });
    return;
  }

  if (targetOrigin === '*' && process.env.NODE_ENV === 'production') {
    // Never broadcast to '*' in production, even if a caller explicitly requests it.
    console.warn('sendMessageToParent: refusing to post to "*" in production', { type });
    return;
  }

  try {
    const message = {
      type,
      data,
      timestamp: Date.now(),
    };
    
    window.parent.postMessage(message, targetOrigin);
    console.log('Sent message to parent:', message);
  } catch (error) {
    console.error('Failed to send message to parent:', error);
  }
};

/**
 * Send error message to parent
 * @param {string} error - Error message
 * @param {string} originalType - Original message type that caused the error
 * @param {string} targetOrigin - Concrete target origin for postMessage.
 */
export const sendErrorToParent = (error, originalType = null, targetOrigin = _cachedParentOrigin) => {
  sendMessageToParent(
    MESSAGE_TYPES.ERROR,
    {
      error,
      originalType,
    },
    targetOrigin
  );
};

/**
 * Send success confirmation to parent
 * @param {string} messageType - Message type (THEME_UPDATED, LOGO_UPDATED, etc.)
 * @param {string} targetOrigin - Concrete target origin for postMessage.
 */
export const sendSuccessToParent = (messageType, targetOrigin = _cachedParentOrigin) => {
  sendMessageToParent(
    messageType,
    {
      success: true,
    },
    targetOrigin
  );
};

/**
 * Validate message structure
 * @param {Object} message - Message object to validate
 * @returns {boolean} True if valid
 */
const validateMessage = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }
  
  if (!message.type || typeof message.type !== 'string') {
    return false;
  }
  
  return true;
};

/**
 * Create message handler for incoming postMessage events
 * @param {Function} onUpdateTheme - Callback for UPDATE_THEME messages
 * @param {Function} onUpdateLogo - Callback for UPDATE_LOGO messages
 * @param {Function} onUpdateTabs - Callback for UPDATE_TABS messages
 * @param {Function} onGetCurrentConfig - Callback for GET_CURRENT_CONFIG messages
 * @param {string|Array<string>} allowedOrigins - Allowed origins for messages.
 *   Defaults to the explicit allow-list from getAllowedOrigins(); '*' is only
 *   used in local development when nothing is configured.
 * @returns {Function} Cleanup function to remove event listener
 */
export const createMessageHandler = (
  onUpdateTheme,
  onUpdateLogo,
  onUpdateTabs,
  onGetCurrentConfig,
  allowedOrigins = getAllowedOrigins(),
  onConfigDeployed = null
) => {
  const handleMessage = (event) => {
    // Filter out webpack dev server messages and other non-protocol messages
    const messageType = event.data?.type;
    
    // Ignore webpack messages and other development server messages
    if (messageType === 'webpackWarnings' || 
        messageType === 'webpackOk' || 
        messageType === 'webpackInvalid' ||
        messageType === 'webpackStillOk' ||
        messageType === 'webpackCompiling' ||
        messageType === 'webpackErrors' ||
        !messageType) {
      // Silently ignore these messages - they're not part of our protocol
      return;
    }

    // Cache parent origin from any valid message
    _cachedParentOrigin = event.origin;

    // Only log messages that match our protocol
    console.log('📥 Gallery received postMessage:', { 
      origin: event.origin, 
      type: messageType, 
      data: event.data,
      allowedOrigins 
    });

    // Validate origin if specified
    // If allowedOrigins is '*', accept all origins (skip validation)
    if (allowedOrigins === '*') {
      // Accept all origins - no validation needed
    } else if (allowedOrigins && allowedOrigins.length > 0) {
      const origins = Array.isArray(allowedOrigins) ? allowedOrigins : [allowedOrigins];
      // Check if origin matches exactly
      const originMatches = origins.includes(event.origin);
      
      if (!originMatches) {
        console.warn('⚠️ Message from unauthorized origin:', event.origin, 'Expected one of:', origins);
        return;
      }
    }

    // Validate message structure
    if (!validateMessage(event.data)) {
      console.warn('⚠️ Invalid message structure:', event.data);
      return;
    }

    // Check if message type is part of our protocol
    const validMessageTypes = Object.values(MESSAGE_TYPES);
    if (!validMessageTypes.includes(messageType)) {
      console.warn('⚠️ Unknown message type (not part of protocol):', messageType);
      return;
    }

    const { type, data } = event.data;
    console.log('✅ Processing message:', { type, data });

    try {
      switch (type) {
        case MESSAGE_TYPES.UPDATE_THEME:
          console.log('🎨 Handling UPDATE_THEME message');
          if (onUpdateTheme && typeof onUpdateTheme === 'function') {
            onUpdateTheme(data, event.origin);
          } else {
            console.warn('⚠️ No handler for UPDATE_THEME');
          }
          break;

        case MESSAGE_TYPES.UPDATE_LOGO:
          if (onUpdateLogo && typeof onUpdateLogo === 'function') {
            onUpdateLogo(data, event.origin);
          }
          break;

        case MESSAGE_TYPES.UPDATE_TABS:
          if (onUpdateTabs && typeof onUpdateTabs === 'function') {
            onUpdateTabs(data, event.origin);
          }
          break;

        case MESSAGE_TYPES.GET_CURRENT_CONFIG:
          if (onGetCurrentConfig && typeof onGetCurrentConfig === 'function') {
            onGetCurrentConfig(event.origin);
          }
          break;

        case MESSAGE_TYPES.CONFIG_DEPLOYED:
          if (onConfigDeployed && typeof onConfigDeployed === 'function') {
            onConfigDeployed(data, event.origin);
          }
          break;

        default:
          console.warn('Unknown message type:', type);
      }
    } catch (error) {
      console.error('Error handling message:', error);
      sendErrorToParent(error.message, type, event.origin);
    }
  };

  // Add event listener
  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};
